import React, { Component, type ChangeEvent } from 'react';
import noop from 'lodash/noop';
import urlParse from 'url-parse';
import Form, { Field, ErrorMessage } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type {
	IntlShapeV2 as IntlShape,
	MessageDescriptorV2 as MessageDescriptor,
} from '@atlassian/jira-intl/src/v2/types.tsx';
import { INVALID_URL } from '../../const.tsx';
import { urlSanityCheck } from '../../util/index.tsx';
import ProTip from '../pro-tip/index.tsx';

export type Props = {
	invalidUrlError?: MessageDescriptor;
	name: string;
	nameCaption: MessageDescriptor;
	namePlaceholder: MessageDescriptor;
	url: string;
	urlAndNameLocked: boolean;
	urlCaption: MessageDescriptor;
	urlPlaceholder: MessageDescriptor;
	onChange: (arg1: { url: string; name: string; hasError?: boolean }) => void;
	intl: IntlShape;
};

export type State = {
	url: string;
	name: string;
	hasUrlError: boolean;
};

// eslint-disable-next-line jira/react/no-class-components
class SmartAdd extends Component<Props, State> {
	static defaultProps = {
		url: '',
		name: '',
		urlAndNameLocked: true,
		onChange: noop,
	};

	constructor(props: Props) {
		// @ts-expect-error - TS2554 - Expected 1-2 arguments, but got 0.
		super();
		const { url, name } = props;
		this.state = {
			url,
			name,
			hasUrlError: false,
		};
	}

	componentDidUpdate(prevProps: Props, prevState: State) {
		// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'State'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Readonly<State>'.
		if (Object.keys(this.state).some((key) => prevState[key] !== this.state[key])) {
			const { onChange } = this.props;
			const { name, url, hasUrlError } = this.state;
			onChange({ name, url, hasError: hasUrlError });
		}
	}

	validate = (value: string | undefined) => {
		if (value === undefined) {
			return undefined;
		}
		const isUrlSane = urlSanityCheck(value);
		if (isUrlSane) {
			const { name } = this.state;
			if (this.props.urlAndNameLocked && name.length === 0) {
				// This prop copies the host of the url as the name, as a kind of prefill behaviour
				const parsedUrl = urlParse(value, {});
				this.setState({
					url: value,
					name: parsedUrl.host ? parsedUrl.host : value,
					hasUrlError: false,
				});
			} else {
				this.setState({
					url: value,
					hasUrlError: false,
				});
			}
		} else {
			this.setState({
				url: value,
				hasUrlError: true,
			});
			return INVALID_URL;
		}
		return undefined;
	};

	onNameChanged = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
		this.setState({
			name: value,
		});
	};

	render() {
		const {
			urlCaption,
			urlPlaceholder,
			nameCaption,
			namePlaceholder,
			invalidUrlError,
			intl: { formatMessage },
		} = this.props;

		const { name, url } = this.state;

		return (
			// Replace with lodash/noop
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			<Form onSubmit={() => {}}>
				{({ formProps }) => (
					<form {...formProps}>
						<Field isRequired label={formatMessage(urlCaption)} name="url" validate={this.validate}>
							{({ fieldProps, error }) => (
								<>
									<Textfield
										{...fieldProps}
										placeholder={formatMessage(urlPlaceholder)}
										value={url}
										type="url"
									/>
									{error === INVALID_URL && invalidUrlError != null && (
										<ErrorMessage>{formatMessage(invalidUrlError)}</ErrorMessage>
									)}
								</>
							)}
						</Field>
						<Field
							isRequired
							label={formatMessage(nameCaption)}
							name="description"
							// @ts-expect-error - TS2322 - Type '{ children: ({ fieldProps }: { fieldProps: FieldProps<string, HTMLInputElement>; error?: string | undefined; valid: boolean; meta: Meta; }) => Element; isRequired: true; label: string; name: string; value: string; }' is not assignable to type 'IntrinsicAttributes & FieldComponentProps<string, HTMLInputElement>'.
							value={name}
						>
							{({ fieldProps }) => (
								<Textfield
									{...fieldProps}
									placeholder={formatMessage(namePlaceholder)}
									onChange={this.onNameChanged}
									value={name}
									aria-describedby="pro-tip-description"
								/>
							)}
						</Field>
						<ProTip id="pro-tip-description" />
					</form>
				)}
			</Form>
		);
	}
}

export default injectIntl(SmartAdd);
